import { useState } from "react"
import styled from "styled-components"
import { X, Calendar as CalendarIcon, Trash2 } from "lucide-react"
import { format } from "date-fns"
import { ko } from "date-fns/locale"
import { toast } from "react-hot-toast"
import { confirmDialog } from "../../utils/confirmDialog"
import { getTaskStatus } from "../../utils/taskStatus"
import Calendar from "./calendar"

const wouldCreateCycle = (allTasks, currentTaskId, candidatePredecessorId) => {
  if (currentTaskId === candidatePredecessorId) return true
  const byId = new Map(allTasks.map((t) => [t.id, t]))
  const visited = new Set()
  const stack = [candidatePredecessorId]
  while (stack.length) {
    const id = stack.pop()
    if (visited.has(id)) continue
    visited.add(id)
    const t = byId.get(id)
    if (!t) continue
    const deps = t.dependencies || []
    if (deps.includes(currentTaskId)) return true
    for (const d of deps) stack.push(d)
  }
  return false
}

const Panel = styled.aside`
  width: 100%;
  height: 100%;
  background: white;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`

const Title = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.01em;
`

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;

  &:hover { background: #f1f5f9; color: #0f172a; }
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 20px 8px;
`

const Section = styled.section`
  & + & {
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px solid #f1f5f9;
  }
`

const SectionTitle = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  margin-bottom: 12px;
`

const Field = styled.div`
  display: grid;
  grid-template-columns: 70px 1fr;
  align-items: center;
  gap: 12px;

  & + & { margin-top: 10px; }
`

const FieldLabel = styled.label`
  font-size: 12.5px;
  font-weight: 500;
  color: #64748b;
`

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  font-size: 13.5px;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }
`

const Textarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  font-size: 13.5px;
  font-family: inherit;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  resize: vertical;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }
`

const DateButton = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  font-size: 13.5px;
  font-weight: 500;
  color: #0f172a;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.12s;
  text-align: left;

  svg { color: #64748b; flex-shrink: 0; }

  &:hover { border-color: #cbd5e1; }
`

const DatePickerPopover = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.12);
  padding: 8px;
`

const Slider = styled.input.attrs({ type: "range" })`
  flex: 1;
  height: 4px;
  -webkit-appearance: none;
  background: #e2e8f0;
  border-radius: 999px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #2563eb;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 1px 2px rgba(37, 99, 235, 0.35);
  }
`

const ProgressValue = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  color: #2563eb;
  min-width: 38px;
  text-align: right;
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  border: 1px solid ${(p) => p.$border};
`

const AssigneeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`

const AssigneeChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px 4px 4px;
  font-size: 12.5px;
  font-weight: 500;
  color: ${(p) => (p.$active ? "#1d4ed8" : "#475569")};
  background: ${(p) => (p.$active ? "#eff6ff" : "white")};
  border: 1px solid ${(p) => (p.$active ? "#bfdbfe" : "#e2e8f0")};
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover { background: ${(p) => (p.$active ? "#dbeafe" : "#f8fafc")}; border-color: #cbd5e1; }

  img {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
  }
`

const ColorRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`

const ColorDot = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.$active ? "#0f172a" : "transparent")};
  background: ${(p) => p.$color};
  cursor: pointer;
  transition: transform 0.08s;
  padding: 0;

  &:hover { transform: scale(1.08); }
`

const DepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fafbfc;
`

const DepRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #0f172a;
  cursor: pointer;

  input { accent-color: #2563eb; }
`

const Footer = styled.div`
  padding: 14px 20px 18px;
  border-top: 1px solid #f1f5f9;
  flex-shrink: 0;
`

const DeleteBtn = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 14px;
  font-size: 13.5px;
  font-weight: 700;
  color: #dc2626;
  background: white;
  border: 1px solid #fecaca;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  letter-spacing: -0.01em;

  &:hover {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #b91c1c;
  }
`

const daysBetween = (a, b) => {
  if (!a || !b) return 0
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate())
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate())
  return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1)
}

export default function TaskDetailsPanel({
  task,
  tasks,
  users,
  taskTypes,
  referenceDate,
  onUpdate,
  onDelete,
  onClose,
}) {
  const [showStartPicker, setShowStartPicker] = useState(false)
  const [showEndPicker, setShowEndPicker] = useState(false)

  if (!task) return null

  const status = getTaskStatus(task, referenceDate)
  const updateField = (field, value) => onUpdate({ ...task, [field]: value })

  const colorPalette = taskTypes && taskTypes.length > 0
    ? taskTypes.map((t) => t.color)
    : ["#4EA8DE", "#8884D8", "#82CA9D", "#FFBB28", "#FF8042"]

  return (
    <Panel>
      <Header>
        <Title>작업 정보</Title>
        <CloseBtn onClick={onClose} title="닫기"><X size={18} /></CloseBtn>
      </Header>

      <Body>
        <Section>
          <SectionTitle>기본 정보</SectionTitle>
          <Field>
            <FieldLabel>이름</FieldLabel>
            <Input value={task.name} onChange={(e) => updateField("name", e.target.value)} />
          </Field>
          <Field>
            <FieldLabel>담당자</FieldLabel>
            <AssigneeRow>
              {users.map((u) => (
                <AssigneeChip
                  key={u.id}
                  $active={task.assignee === u.id}
                  onClick={() => updateField("assignee", u.id)}
                  title={u.name}
                >
                  <img src={u.avatar || "/resource/defaultImg.png"} alt={u.name} />
                  {u.name}
                </AssigneeChip>
              ))}
            </AssigneeRow>
          </Field>
          <Field>
            <FieldLabel>진행률</FieldLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Slider
                value={task.progress}
                min={0}
                max={100}
                step={5}
                onChange={(e) => updateField("progress", Number.parseInt(e.target.value))}
              />
              <ProgressValue>{task.progress}%</ProgressValue>
            </div>
          </Field>
          <Field>
            <FieldLabel>상태</FieldLabel>
            <div>
              <StatusBadge $bg={status.bg} $color={status.color} $border={status.border}>
                {status.label}
              </StatusBadge>
            </div>
          </Field>
          <Field>
            <FieldLabel>색상</FieldLabel>
            <ColorRow>
              {colorPalette.map((c) => (
                <ColorDot
                  key={c}
                  $color={c}
                  $active={task.color === c}
                  onClick={() => updateField("color", c)}
                />
              ))}
            </ColorRow>
          </Field>
        </Section>

        <Section>
          <SectionTitle>기간</SectionTitle>
          <Field>
            <FieldLabel>시작일</FieldLabel>
            <div style={{ position: "relative" }}>
              <DateButton onClick={() => setShowStartPicker((v) => !v)}>
                <CalendarIcon size={14} />
                {format(task.startDate, "PPP", { locale: ko })}
              </DateButton>
              {showStartPicker && (
                <DatePickerPopover>
                  <Calendar
                    mode="single"
                    selected={task.startDate}
                    onSelect={(date) => {
                      updateField("startDate", date)
                      setShowStartPicker(false)
                    }}
                  />
                </DatePickerPopover>
              )}
            </div>
          </Field>
          <Field>
            <FieldLabel>종료일</FieldLabel>
            <div style={{ position: "relative" }}>
              <DateButton onClick={() => setShowEndPicker((v) => !v)}>
                <CalendarIcon size={14} />
                {format(task.endDate, "PPP", { locale: ko })}
              </DateButton>
              {showEndPicker && (
                <DatePickerPopover>
                  <Calendar
                    mode="single"
                    selected={task.endDate}
                    onSelect={(date) => {
                      updateField("endDate", date)
                      setShowEndPicker(false)
                    }}
                  />
                </DatePickerPopover>
              )}
            </div>
          </Field>
          <Field>
            <FieldLabel>기간</FieldLabel>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>
              {daysBetween(task.startDate, task.endDate)}일
            </span>
          </Field>
        </Section>

        <Section>
          <SectionTitle>이전 작업 연결</SectionTitle>
          <DepsList>
            {tasks.filter((t) => t.id !== task.id).length === 0 ? (
              <span style={{ fontSize: 12.5, color: "#94a3b8" }}>연결 가능한 작업이 없습니다.</span>
            ) : (
              tasks.filter((t) => t.id !== task.id).map((t) => {
                const checked = (task.dependencies || []).includes(t.id)
                return (
                  <DepRow key={t.id}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const deps = [...(task.dependencies || [])]
                        if (e.target.checked) {
                          if (wouldCreateCycle(tasks, task.id, t.id)) {
                            toast.error(`순환 의존이 발생해 "${t.name}"을 연결할 수 없습니다.`)
                            return
                          }
                          updateField("dependencies", [...deps, t.id])
                        } else {
                          updateField("dependencies", deps.filter((id) => id !== t.id))
                        }
                      }}
                    />
                    {t.name}
                  </DepRow>
                )
              })
            )}
          </DepsList>
        </Section>
      </Body>

      <Footer>
        <DeleteBtn
          onClick={async () => {
            const ok = await confirmDialog(`정말로 "${task.name}" 작업을 삭제하시겠습니까?`)
            if (!ok) return
            onDelete()
          }}
        >
          <Trash2 size={15} /> 작업 삭제
        </DeleteBtn>
      </Footer>
    </Panel>
  )
}
